import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../Auth/AuthContext';
import axios from 'axios';
import { API_CONFIG } from '../../config/api';

const TokenManagement = () => {
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newTokenName, setNewTokenName] = useState('');
  const [showNewToken, setShowNewToken] = useState(false);
  const [newToken, setNewToken] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');

  const { getJwtToken } = useAuth();

  // Controller for API request cancellation
  const [controller, setController] = useState(null);

  // Fetch tokens on component mount
  useEffect(() => {
    fetchTokens();
    
    // Cleanup function to cancel any ongoing requests when unmounting
    return () => {
      if (controller) {
        controller.abort();
      }
    };
  }, []);

  // Fetch all tokens for the user
  const fetchTokens = async () => {
    // Cancel any previous ongoing request
    if (controller) {
      controller.abort();
    }

    // Create a new AbortController for this request
    const newController = new AbortController();
    setController(newController);
    
    try {
      setLoading(true);
      setError(null);
      
      let token;
      try {
        token = await getJwtToken();
      } catch (tokenErr) {
        console.error('Error getting JWT token:', tokenErr);
        setError('Authentication error. Please try signing out and signing in again.');
        setLoading(false);
        return;
      }
      
      const response = await axios.get(`${API_CONFIG.DASHBOARD_API_URL}/tokens`, {
        headers: {
          Authorization: `Bearer ${token}`
        },
        signal: newController.signal
      });
      
      // Only update state if this is the most recent request (not aborted)
      setTokens(response.data.tokens || []);
    } catch (err) {
      // Don't update state if request was aborted
      if (err.name === 'AbortError' || err.name === 'CanceledError') {
        console.log('Token fetch request was cancelled');
        return;
      }
      console.error('Error fetching tokens:', err);
      
      // Detailed error logging
      if (err.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        console.error('Error response data:', err.response.data);
        console.error('Error response status:', err.response.status);
        console.error('Error response headers:', err.response.headers);
        setError(`Failed to fetch tokens: ${err.response.status} ${JSON.stringify(err.response.data)}`);
      } else if (err.request) {
        // The request was made but no response was received
        console.error('Error request:', err.request);
        setError('Failed to fetch tokens: No response received from server. CORS issue or network error.');
      } else {
        // Something happened in setting up the request that triggered an Error
        console.error('Error message:', err.message);
        setError(`Failed to fetch tokens: ${err.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  // Create a new token
  const createToken = async (e) => {
    e.preventDefault();
    
    if (!newTokenName.trim()) {
      setError('Token name is required');
      return;
    }
    
    // Cancel any previous ongoing request
    if (controller) {
      controller.abort();
    }

    // Create a new AbortController for this request
    const newController = new AbortController();
    setController(newController);
    
    try {
      setLoading(true);
      setError(null);
      
      const token = await getJwtToken();
      
      const response = await axios.post(
        `${API_CONFIG.DASHBOARD_API_URL}/tokens`,
        { name: newTokenName },
        {
          headers: {
            Authorization: `Bearer ${token}`
          },
          signal: newController.signal
        }
      );
      
      // Store the new token to display to the user
      setNewToken(response.data);
      setShowNewToken(true);
      setNewTokenName('');
      setSuccessMessage('API token created successfully!');
      
      // Refresh the token list
      fetchTokens();
    } catch (err) {
      // Don't update state if request was aborted
      if (err.name === 'AbortError' || err.name === 'CanceledError') {
        console.log('Token creation request was cancelled');
        return;
      }
      console.error('Error creating token:', err);
      
      // Detailed error logging
      if (err.response) {
        console.error('Error response data:', err.response.data);
        console.error('Error response status:', err.response.status);
        setError(`Failed to create token: ${err.response.status} ${JSON.stringify(err.response.data)}`);
      } else if (err.request) {
        console.error('Error request:', err.request);
        setError('Failed to create token: No response received from server. CORS issue or network error.');
      } else {
        console.error('Error message:', err.message);
        setError(`Failed to create token: ${err.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  // Delete a token
  const deleteToken = async (tokenId) => {
    if (!window.confirm('Are you sure you want to delete this API token? This action cannot be undone.')) {
      return;
    }
    
    // Cancel any previous ongoing request
    if (controller) {
      controller.abort();
    }

    // Create a new AbortController for this request
    const newController = new AbortController();
    setController(newController);
    
    try {
      setLoading(true);
      setError(null);
      
      const token = await getJwtToken();
      
      await axios.delete(
        `${API_CONFIG.DASHBOARD_API_URL}/tokens/${tokenId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          },
          signal: newController.signal
        }
      );
      
      setTokens(tokens.filter(t => t.token_id !== tokenId));
      setSuccessMessage('API token deleted successfully!');
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
    } catch (err) {
      // Don't update state if request was aborted
      if (err.name === 'AbortError' || err.name === 'CanceledError') {
        console.log('Token deletion request was cancelled');
        return;
      }
      console.error('Error deleting token:', err);
      
      // Detailed error logging
      if (err.response) {
        console.error('Error response data:', err.response.data);
        console.error('Error response status:', err.response.status);
        setError(`Failed to delete token: ${err.response.status} ${JSON.stringify(err.response.data)}`);
      } else if (err.request) {
        console.error('Error request:', err.request);
        setError('Failed to delete token: No response received from server. CORS issue or network error.');
      } else {
        console.error('Error message:', err.message);
        setError(`Failed to delete token: ${err.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  // Copy token to clipboard
  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setSuccessMessage('Copied to clipboard!');
      
      // Clear success message after 3 seconds
      const timeoutId = setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
      
      // Store the timeout ID for cleanup
      return () => clearTimeout(timeoutId);
    } catch (err) {
      console.error('Failed to copy:', err);
      setError('Failed to copy to clipboard');
    }
  };

  // Format date from timestamp or ISO string
  const formatDate = (dateValue) => {
    if (!dateValue) return 'N/A';
    
    try {
      // Check if it's a timestamp (number) or ISO string
      const date = typeof dateValue === 'number' 
        ? new Date(dateValue * 1000)  // Unix timestamp
        : new Date(dateValue);        // ISO string
      
      // Check if date is valid
      if (isNaN(date.getTime())) {
        console.error('Invalid date value:', dateValue);
        return 'Invalid Date';
      }
      
      return date.toLocaleString();
    } catch (err) {
      console.error('Error formatting date:', err, dateValue);
      return 'Error';
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-bold text-gray-800">API Tokens</h2>
        <button
          onClick={() => {
            setShowNewToken(false);
            setNewToken(null);
          }}
          className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded"
        >
          Create New Token
        </button>
      </div>
      
      {error && (
        <div className="mb-4 p-3 bg-red-100 text-red-700 rounded">
          <p className="font-bold">Error:</p>
          <p>{error}</p>
        </div>
      )}
      
      {successMessage && (
        <div className="mb-4 p-3 bg-green-100 text-green-700 rounded">
          {successMessage}
        </div>
      )}
      
      {/* New Token Form */}
      {!showNewToken ? (
        <form onSubmit={createToken} className="mb-6 p-4 border border-gray-200 rounded">
          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="tokenName">
              Token Name
            </label>
            <input
              id="tokenName"
              type="text"
              value={newTokenName}
              onChange={(e) => setNewTokenName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="e.g., Production API, Test Environment"
              required
            />
          </div>
          
          <button
            type="submit"
            disabled={loading}
            className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded disabled:opacity-50"
          >
            {loading ? 'Creating...' : 'Create Token'}
          </button>
        </form>
      ) : newToken && (
        <div className="mb-6 p-4 border border-green-200 bg-green-50 rounded">
          <h3 className="font-bold text-green-800 mb-2">New API Token Created</h3>
          <p className="text-sm text-gray-600 mb-2">
            This is the only time your full token will be shown. Please copy it now.
          </p>
          
          <div className="flex items-center mb-4">
            <input
              type="text"
              value={newToken.token}
              readOnly
              className="flex-grow px-3 py-2 border border-gray-300 rounded-l focus:outline-none"
            />
            <button
              onClick={() => copyToClipboard(newToken.token)}
              className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-r"
            >
              Copy
            </button>
          </div>
          
          <div className="text-sm text-gray-600">
            <p><strong>Token ID:</strong> {newToken.token_id}</p>
            <p><strong>Name:</strong> {newToken.name}</p>
            <p><strong>Created:</strong> {formatDate(newToken.created_at)}</p>
          </div>
          
          <button
            onClick={() => {
              setShowNewToken(false);
              setNewToken(null);
            }}
            className="mt-4 text-blue-500 hover:text-blue-700"
          >
            Done
          </button>
        </div>
      )}
      
      {/* Tokens List */}
      {loading && !newToken ? (
        <div className="text-center py-4">Loading tokens...</div>
      ) : tokens.length === 0 ? (
        <div className="text-center py-4 text-gray-500">
          No API tokens found. Create your first token to get started.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white">
            <thead>
              <tr className="bg-gray-100 text-gray-600 uppercase text-sm leading-normal">
                <th className="py-3 px-6 text-left">Name</th>
                <th className="py-3 px-6 text-left">Token Preview</th>
                <th className="py-3 px-6 text-left">Created</th>
                <th className="py-3 px-6 text-left">Last Used</th>
                <th className="py-3 px-6 text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="text-gray-600 text-sm">
              {tokens.map((token) => (
                <tr key={token.token_id} className="border-b border-gray-200 hover:bg-gray-50">
                  <td className="py-3 px-6 text-left whitespace-nowrap">
                    {token.name}
                  </td>
                  <td className="py-3 px-6 text-left">
                    <code className="bg-gray-100 px-2 py-1 rounded font-mono text-xs">
                      {token.token_preview || 'N/A'}
                    </code>
                  </td>
                  <td className="py-3 px-6 text-left">
                    {formatDate(token.created_at)}
                  </td>
                  <td className="py-3 px-6 text-left">
                    {formatDate(token.last_used)}
                  </td>
                  <td className="py-3 px-6 text-center">
                    <button
                      onClick={() => deleteToken(token.token_id)}
                      className="text-red-500 hover:text-red-700"
                      title="Delete Token"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      
      <div className="mt-6 text-sm text-gray-500">
        <h3 className="font-bold mb-2">How to use your API token</h3>
        <p className="mb-2">
          Include your API token in the <code className="bg-gray-100 px-1 rounded">X-Api-Key</code> header with your requests:
        </p>
        <pre className="bg-gray-100 p-3 rounded overflow-x-auto">
          {`curl -X POST https://api.describeapi.com/describe/text \\
  -H "Content-Type: application/json" \\
  -H "X-Api-Key: YOUR_API_KEY" \\
  -d '{"image": "base64_encoded_image_data"}'`}
        </pre>
      </div>
    </div>
  );
};

export default TokenManagement;
